import type { JsonMap, JSON } from './json'

export type ExprLiteral = string | number | boolean | null

export type Expr =
  | { type: 'literal'; value: ExprLiteral }
  | { type: 'identifier'; nameStart: number; nameEnd: number; name: string | undefined }
  | { type: 'call'; nameStart: number; nameEnd: number; name: string | undefined; args: Expr[] }

export type ExprFunctions = Record<string, (...args: JSON[]) => JSON>

export type ParsedExpr = {
  source: string
  expr: Expr
}

export function parseExpr(source: string): ParsedExpr | undefined {
  if (source.trim().length == 0) return undefined
  const parser = new ExprParser(source)
  const expr = parser.parse()
  return expr ? { source, expr } : undefined
}

export function evaluateExpr(parsed: ParsedExpr, vars: JsonMap, fns: ExprFunctions): JSON {
  return evaluateExprNode(parsed.expr, parsed.source, vars, fns)
}

function evaluateExprNode(expr: Expr, source: string, vars: JsonMap, fns: ExprFunctions): JSON {
  switch (expr.type) {
    case 'literal':
      return expr.value
    case 'identifier':
      return vars[getName(expr, source)]
    case 'call':
      const fn = fns[getName(expr, source)]
      const args = expr.args.map((arg) => evaluateExprNode(arg, source, vars, fns))
      return fn(...args)
  }
}

class ExprParser {
  private source: string
  private sourceLength: number
  private index = 0
  private failed = false

  constructor(source: string) {
    this.source = source
    this.sourceLength = source.length
  }

  parse(): Expr | undefined {
    const expr = this.parseExpression()
    if (!expr || this.failed) return undefined
    this.skipWhitespace()
    return this.isAtEnd() && !this.failed ? expr : undefined
  }

  private parseExpression(): Expr | undefined {
    this.skipWhitespace()
    if (this.isAtEnd()) {
      return this.fail()
    }

    const code = this.peekCharCode()
    if (code == 34) return this.parseString()
    if (this.isNumberStartCode(code)) return this.parseNumber()
    if (this.isIdentifierStartCode(code)) {
      const start = this.index
      const end = this.readIdentifierEnd()
      if (this.failed) return undefined
      const nameLength = end - start
      if (nameLength == 4 && this.matchKeyword(start, end, 'true')) {
        return { type: 'literal', value: true }
      }
      if (nameLength == 5 && this.matchKeyword(start, end, 'false')) {
        return { type: 'literal', value: false }
      }
      if (nameLength == 4 && this.matchKeyword(start, end, 'null')) {
        return { type: 'literal', value: null }
      }

      this.skipWhitespace()
      if (this.peekCharCode() == 40) {
        return this.parseCallFromSpan(start, end)
      }

      return { type: 'identifier', nameStart: start, nameEnd: end, name: undefined }
    }

    return this.fail()
  }

  private parseCallFromSpan(nameStart: number, nameEnd: number): Expr | undefined {
    this.index += 1
    const args: Expr[] = []
    this.skipWhitespace()
    if (this.peekCharCode() == 41) {
      this.index += 1
      return { type: 'call', nameStart, nameEnd, name: undefined, args }
    }

    while (true) {
      const expr = this.parseExpression()
      if (!expr) return undefined
      args.push(expr)
      this.skipWhitespace()

      const code = this.peekCharCode()
      if (code == 44) {
        this.index += 1
        continue
      }

      if (code == 41) {
        this.index += 1
        break
      }

      return this.fail()
    }

    return { type: 'call', nameStart, nameEnd, name: undefined, args }
  }

  private parseNumber(): Expr | undefined {
    const start = this.index
    if (this.peek() == '-') {
      this.index += 1
    }

    this.readDigits()

    if (this.peek() == '.') {
      this.index += 1
      this.readDigits()
    }

    const raw = this.source.slice(start, this.index)
    const value = Number(raw)
    if (Number.isNaN(value)) {
      return this.fail()
    }

    return { type: 'literal', value }
  }

  private parseString(): Expr | undefined {
    const source = this.source
    const length = this.sourceLength
    let i = this.index + 1
    const start = i
    let hasEscape = false

    while (i < length) {
      const code = source.charCodeAt(i)
      if (code == 34) {
        const raw = source.slice(start, i)
        this.index = i + 1
        if (!hasEscape) {
          return { type: 'literal', value: raw }
        }
        return { type: 'literal', value: this.unescapeString(raw) }
      }
      if (code == 92) {
        hasEscape = true
        i += 1
        if (i >= length) break
      }
      i += 1
    }

    this.index = i
    return this.fail()
  }

  private unescapeString(raw: string) {
    let result = ''
    for (let i = 0; i < raw.length; i += 1) {
      const code = raw.charCodeAt(i)
      if (code != 92) {
        result += raw[i]
        continue
      }
      i += 1
      if (i >= raw.length) break
      const next = raw.charCodeAt(i)
      switch (next) {
        case 110:
          result += '\n'
          break
        case 114:
          result += '\r'
          break
        case 116:
          result += '\t'
          break
        case 92:
          result += '\\'
          break
        case 34:
          result += '"'
          break
        default:
          result += raw[i]
          break
      }
    }
    return result
  }

  private readIdentifierEnd(): number {
    const source = this.source
    const length = this.sourceLength
    let i = this.index + 1
    while (i < length) {
      const code = source.charCodeAt(i)
      if (!this.isIdentifierPartCode(code)) {
        break
      }
      i += 1
    }
    this.index = i
    return i
  }

  private readDigits() {
    while (!this.isAtEnd() && this.isDigitCode(this.peekCharCode())) {
      this.index += 1
    }
  }

  private skipWhitespace() {
    const source = this.source
    const length = this.sourceLength
    let i = this.index

    while (i < length) {
      const code = source.charCodeAt(i)
      if (code != 32 && code != 10 && code != 13 && code != 9) {
        break
      }
      i += 1
    }

    this.index = i
  }

  private expect(char: string) {
    if (this.peek() != char) {
      this.fail()
      return
    }
    this.index += 1
  }

  private peek() {
    return this.source[this.index]
  }

  private peekCharCode() {
    if (this.index >= this.sourceLength) return -1
    return this.source.charCodeAt(this.index)
  }

  private isAtEnd() {
    return this.index >= this.sourceLength
  }

  private isDigitCode(code: number) {
    return code >= 48 && code <= 57
  }

  private isWhitespaceCode(code: number) {
    return code == 32 || code == 10 || code == 13 || code == 9
  }

  private isNumberStartCode(code: number) {
    if (code == 45) {
      const next = this.index + 1 < this.sourceLength ? this.source.charCodeAt(this.index + 1) : -1
      return this.isDigitCode(next)
    }
    return this.isDigitCode(code)
  }

  private isIdentifierStartCode(code: number) {
    return (
      (code >= 65 && code <= 90) ||
      (code >= 97 && code <= 122) ||
      code == 95 ||
      code == 36
    )
  }

  private isIdentifierPartCode(code: number) {
    return this.isIdentifierStartCode(code) || this.isDigitCode(code)
  }

  private matchKeyword(start: number, end: number, keyword: string) {
    if (end - start != keyword.length) return false
    for (let i = 0; i < keyword.length; i += 1) {
      if (this.source.charCodeAt(start + i) != keyword.charCodeAt(i)) {
        return false
      }
    }
    return true
  }

  private fail() {
    this.failed = true
    return undefined
  }
}

function getName(expr: { nameStart: number; nameEnd: number; name: string | undefined }, source: string) {
  if (expr.name != undefined) {
    return expr.name
  }
  const name = source.slice(expr.nameStart, expr.nameEnd)
  expr.name = name
  return name
}
